import { SHStack, SHTypography } from "@components/design-systems";
import { DELAY_TIME } from "@constants";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  AnalysisSummaryDTO,
  RenderAnalysisSummaryItemProps,
} from "@models/platform-analysis/entities/steps/summary";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { useTheme } from "@mui/material";
import { sortBySubProductOrder } from "@pages/platform-analysis-v2/_id/utils";
import { RootState } from "@redux/store";
import { hexToRGBA } from "@utils";
import { debounce } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";
import { FeeAnalysisHeader } from "../../../fee/analysis/components/fee-analysis-header";
import { FeeChartSection } from "../../../fee/analysis/components/fee-chart-section";

export const SummaryFeeEstimates = memo(
  ({
    name,
    userType,
    disabled: sectionDisabled,
  }: RenderAnalysisSummaryItemProps) => {
    const { palette } = useTheme();

    const { setValue } = useFormContext<AnalysisSummaryDTO>();
    const { setSummaryHiddenIds, setSummaryFeeFilters } = usePlatformAnalysis();

    const storedHideFeeChart = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary?.hideFeeAnalysisGraph,
    );
    const storedShortlistedFeeAnalysisSubProducts = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary?.feeAnalysis
          ?.shortlisted?.subProducts,
    );
    const storedProductRecommendations = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.summary?.productRecommendations,
    );

    const [hideChart, toggleChartHidden] = useToggle(!!storedHideFeeChart);

    const handleOnChartVisibilityChange = (value: boolean) => {
      toggleChartHidden(value);
      setValue("hideFeeAnalysisGraph", value, { shouldDirty: true });
    };

    const handleOnHiddenIdsChange = (hiddenIds: string[]) => {
      if (sectionDisabled) return;

      const sortedSubProducts = sortBySubProductOrder({
        data: storedShortlistedFeeAnalysisSubProducts || [],
        getProductId: (item) => item.productId,
        productRecommendations: storedProductRecommendations,
      });

      setSummaryHiddenIds(hiddenIds, sortedSubProducts);
    };

    const handleOnChangeHeatmapMode = useCallback(
      (value: FeeHeatmapMode) => {
        setValue("feeTableViewMode", value, { shouldDirty: true });
      },
      [setValue],
    );

    const handleOnChangeDisplayStyle = useCallback(
      (value: FeesDisplayStyle) => {
        setValue("feesDisplayStyle", value, { shouldDirty: true });
      },
      [setValue],
    );

    const handleOnChangeFilter = useCallback(
      (
        investmentMenuValue: FeeInvestmentMenuOption,
        feeMissingInvestmentsStyle: FeeMissingInvestmentsOption,
      ) => {
        setValue("investmentMenu", investmentMenuValue, {
          shouldDirty: true,
        });

        setValue("feeMissingInvestmentsStyle", feeMissingInvestmentsStyle, {
          shouldDirty: true,
        });

        const sortedSubProducts = sortBySubProductOrder({
          data: storedShortlistedFeeAnalysisSubProducts || [],
          productRecommendations: storedProductRecommendations,
          getProductId: (item) => item.productId,
        });

        setSummaryFeeFilters({
          investmentMenuValue,
          missingInvestmentValue: feeMissingInvestmentsStyle,
          sortedSubProducts,
        });
        setSummaryHiddenIds([], sortedSubProducts);
      },
      [
        setValue,
        storedShortlistedFeeAnalysisSubProducts,
        storedProductRecommendations,
        setSummaryFeeFilters,
        setSummaryHiddenIds,
      ],
    );

    const debouncedHandleOnChangeFilter = useMemo(
      () => debounce(handleOnChangeFilter, DELAY_TIME),
      [handleOnChangeFilter],
    );

    const debouncedHandleOnChangeHeatmapMode = useMemo(
      () => debounce(handleOnChangeHeatmapMode, DELAY_TIME),
      [handleOnChangeHeatmapMode],
    );

    const debouncedHandleOnChangeDisplayStyle = useMemo(
      () => debounce(handleOnChangeDisplayStyle, DELAY_TIME),
      [handleOnChangeDisplayStyle],
    );

    return (
      <SHStack>
        <SHStack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <SHTypography variant="subtitle2">{name?.[userType]}</SHTypography>
          <FeeAnalysisHeader
            removeTitle
            disabled={sectionDisabled}
            onChangeFilter={debouncedHandleOnChangeFilter}
            onChangeHeatmapMode={debouncedHandleOnChangeHeatmapMode}
            onChangeDisplayStyle={debouncedHandleOnChangeDisplayStyle}
          />
        </SHStack>

        <SHStack gap={2}>
          <FeeChartSection
            disabled={sectionDisabled}
            allowHideChart
            hideChart={hideChart}
            onChangeHiddenIds={handleOnHiddenIdsChange}
            onChartVisibilityChange={handleOnChartVisibilityChange}
            getTotalPortfolioValue={(state: RootState) =>
              state.platformAnalysis?.platformAnalysis?.summary
                ?.feePortfolioDetails?.totalPortfolioValue
            }
            getAnalysisStepFeeData={(state: RootState) =>
              state.platformAnalysis?.platformAnalysis?.summary
            }
            getContainerData={(state: RootState) =>
              state.platformAnalysis?.platformAnalysis?.summary?.feeAnalysis
                ?.shortlisted
            }
            getStoredProductRecommendations={(state: RootState) =>
              state.platformAnalysis?.internal?.summary?.productRecommendations
            }
            heatmapProps={{
              sx: {
                p: "15px 0 0 0",
                backgroundColor: "transparent",
                "& .FeeChartSection-heatmap-title": {
                  border: `solid 1px ${hexToRGBA(palette.primary[50], 0.3)}`,
                  p: "10px 15px",
                },
              },
            }}
            feeLineChartProps={{
              containerSx: {
                backgroundColor: "transparent",
                border: "none",
                paddingX: 0,
                "& .FeeLineChart-title": {
                  border: `solid 1px ${hexToRGBA(palette.primary[50], 0.3)}`,
                  p: "10px 15px",
                },
              },
            }}
          />
        </SHStack>
      </SHStack>
    );
  },
);
